import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Search, BookText, Plus } from "lucide-react";
import { toast } from "sonner";

export default function GlossaryPage() {
  const { glossary } = useAuth();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kavram: "", tanim: "" });
  const [saving, setSaving] = useState(false);
  const filtered = glossary.filter((g) => g.kavram.toLowerCase().includes(q.toLowerCase()) || g.tanim.toLowerCase().includes(q.toLowerCase()));

  const submit = async () => {
    if (!form.kavram.trim() || !form.tanim.trim()) { toast.error("Kavram ve tanım gerekli."); return; }
    setSaving(true);
    try {
      await api.post("/glossary/suggest", form);
      toast.success("Öneriniz admin onayına gönderildi. Onaylanınca +15 AP kazanacaksınız.");
      setForm({ kavram: "", tanim: "" }); setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="fade-up flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-terra flex items-center gap-2"><BookText size={14} /> Kavram Dizini</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mt-1">Felsefi Sözlük</h1>
          <p className="text-ink-2 mt-2">Yorumlarda geçen terimlerin anlamlarını burada bulabilirsin.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-glossary-btn" className="bg-terra hover:bg-terra-hover text-white gap-1.5 shrink-0"><Plus size={16} /> Madde Ekle</Button>
          </DialogTrigger>
          <DialogContent className="bg-surface max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Yeni Sözlük Maddesi Öner</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">Önerin admin onayından sonra yayına girer ve <span className="text-terra font-medium">+15 AP</span> kazanırsın.</p>
            <div className="space-y-3 mt-2">
              <div><Label>Kavram</Label><Input data-testid="suggest-term" value={form.kavram} onChange={(e) => setForm({ ...form, kavram: e.target.value })} className="mt-1" placeholder="Örn: Diyalektik" /></div>
              <div><Label>Tanım</Label><Textarea data-testid="suggest-def" value={form.tanim} onChange={(e) => setForm({ ...form, tanim: e.target.value })} rows={5} className="mt-1" placeholder="Kavramın felsefi anlamını açıkla..." /></div>
            </div>
            <DialogFooter><Button onClick={submit} disabled={saving} data-testid="submit-glossary" className="bg-terra hover:bg-terra-hover text-white">{saving ? "Gönderiliyor..." : "Onaya Gönder"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="relative mt-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input data-testid="glossary-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kavram ara..." className="pl-10 h-11 bg-surface" />
      </div>
      <div className="mt-6 space-y-3">
        {filtered.map((g) => (
          <div key={g.id} className="border border-line rounded-lg p-5 bg-surface" data-testid={`glossary-${g.id}`}>
            <h3 className="font-serif text-2xl text-terra">{g.kavram}</h3>
            <p className="text-ink-2 mt-1 leading-relaxed">{g.tanim}</p>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-muted-foreground text-sm">Kavram bulunamadı.</p>}
      </div>
    </div>
  );
}
